import { useState } from "react";

import type { ImportItemResult, ImportSummaryResponse } from "@synthesis/shared-services/internal/application/types";
import { IconCheckmarkCircleFill, IconCloseCircleFill, IconFileAdd, IconFileFill } from "@synthesis/shared-icons";
import { FileIconComponent, ScrollArea } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { hasSuccessfulImportItems, isImportItemFailed, mapImportItemToResultData } from "./import-application-result.lib";
import { ImportApplicationErrorsView, ImportApplicationResultView } from "./import-application-result-view";

type ImportApplicationsResultViewProps = {
  fileName: string;
  summary: ImportSummaryResponse;
  className?: string;
};

const getImportItemKey = (item: ImportItemResult, index: number) => item.externalRequestId ?? `${item.number ?? "item"}-${index}`;

const getItemTitle = (item: ImportItemResult) => (item.title?.trim() ? item.title : "Без названия");

type ImportOutcomeIconProps = {
  outcome?: ImportItemResult["outcome"];
  className?: string;
};

/** TODO(api): Figma uses distinct icons per outcome (file-add / file-check / file-forbidden / file2).
 *  Map `outcome` from API once all states are defined; add UNCHANGED or SKIPPED if backend introduces it. */
const ImportOutcomeIcon = ({ outcome, className }: ImportOutcomeIconProps) => {
  const iconClassName = cn("size-5 shrink-0", className);

  switch (outcome) {
    case "CREATED":
      return <IconFileAdd className={cn(iconClassName, "text-system-info-50")} />;
    case "UPDATED":
      return <IconCheckmarkCircleFill className={cn(iconClassName, "text-system-success-50")} />;
    case "FAILED":
      return <IconCloseCircleFill className={cn(iconClassName, "text-system-error-50")} />;
    default:
      return <IconFileFill className={cn(iconClassName, "text-neutrals-70")} />;
  }
};

type ImportApplicationsListItemProps = {
  item: ImportItemResult;
  isSelected: boolean;
  onSelect: () => void;
};

const ImportApplicationsListItem = ({ item, isSelected, onSelect }: ImportApplicationsListItemProps) => {
  const isFailed = isImportItemFailed(item);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-1 overflow-hidden rounded-sm px-3 py-1 text-left transition-colors",
        isFailed
          ? isSelected
            ? "bg-system-error-95 text-system-error-50"
            : "bg-neutrals-white text-system-error-50 hover:bg-system-error-95"
          : isSelected
            ? "bg-brand-99 text-brand-40"
            : "bg-neutrals-white text-type-common hover:bg-neutrals-99",
      )}
    >
      <ImportOutcomeIcon outcome={item.outcome} />
      <span className="subtitle-sm min-w-0 flex-1 truncate">{getItemTitle(item)}</span>
    </button>
  );
};

export const ImportApplicationsResultView = ({ fileName, summary, className }: ImportApplicationsResultViewProps) => {
  const items = summary.items ?? [];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showOnlyChanges, setShowOnlyChanges] = useState(false);

  const selectedItem = items[selectedIndex];
  const hasSuccessfulItems = hasSuccessfulImportItems(items);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-4">
        <h2 className="heading-h4 text-type-common">Архив заявок успешно импортирован</h2>
        <div className="flex min-w-0 items-start gap-3 py-2.5">
          <FileIconComponent className="size-6 shrink-0" ext="zip" />
          <span className="paragraph-base text-type-common min-w-0 break-all">{fileName}</span>
        </div>
      </div>

      {items.length > 0 ? (
        hasSuccessfulItems ? (
          <div className="flex min-h-0 flex-1 gap-4">
            <div className="bg-neutrals-99 flex w-[21rem] shrink-0 flex-col overflow-hidden rounded-lg">
              <ScrollArea className="min-h-0 flex-1 py-2 [&>[role='presentation']]:!min-w-full">
                <div className="flex flex-col gap-0.5 px-2">
                  {items.map((item, index) => (
                    <ImportApplicationsListItem
                      key={getImportItemKey(item, index)}
                      item={item}
                      isSelected={index === selectedIndex}
                      onSelect={() => setSelectedIndex(index)}
                    />
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="shadow-d bg-neutrals-white min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg">
              <ScrollArea className="h-full max-h-[32rem] [&>[role='presentation']]:!min-w-full">
                {selectedItem ? (
                  <ImportApplicationResultView
                    variant="detail"
                    data={mapImportItemToResultData(selectedItem)}
                    errors={selectedItem.errors ?? undefined}
                    isImportFailed={isImportItemFailed(selectedItem)}
                    showOnlyChanges={showOnlyChanges}
                    onShowOnlyChangesChange={setShowOnlyChanges}
                    className="p-6"
                  />
                ) : null}
              </ScrollArea>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((item, index) => (
              <div key={getImportItemKey(item, index)} className="flex flex-col gap-2">
                <p className="subtitle-sm text-type-common">{getItemTitle(item)}</p>
                <ImportApplicationErrorsView errors={item.errors ?? []} />
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
};
